import { db } from '../firebase';
import { supabase } from '../supabase';
import {
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs
} from 'firebase/firestore';

/**
 * Firebase-Supabase Bidirectional Sync Service
 * 
 * This service syncs data between Firebase Firestore and Supabase PostgreSQL
 * to ensure data redundancy and prevent data loss.
 */

class DatabaseSyncService {
  constructor() {
    this.syncEnabled = true;
    this.syncQueue = [];
    this.listeners = [];     // Firebase onSnapshot unsubscribe functions
    this.channels = [];      // Supabase realtime channel references
    this.migrationRunning = false;
  }

  /**
   * Convert Firebase timestamp to ISO string for Supabase
   */
  convertTimestamp(timestamp) {
    if (!timestamp) return null;
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp.toDate) return timestamp.toDate().toISOString();
    if (timestamp instanceof Date) return timestamp.toISOString();
    return new Date(timestamp).toISOString();
  }

  /**
   * Map Firebase field names to Supabase column names.
   * Firebase and Supabase schemas can differ — this bridges the gap.
   */
  /**
   * Given a Firebase collection name and document data, return the Supabase
   * table that should store this record.
   *   users (admin/superadmin) → admins
   *   users (customer/anything else) → customers
   *   admins collection → admins
   *   serviceProviders → serviceProviders
   *   bookings → bookings
   *   reviews → reviews
   */
  resolveSupabaseTable(collectionName, data) {
    if (collectionName === 'admins') return 'admins';
    if (collectionName === 'users') {
      const t = (data?.userType || data?.role || '').toLowerCase();
      if (t === 'admin' || t === 'superadmin') return 'admins';
      return 'customers';
    }
    return collectionName; // serviceProviders, bookings, reviews unchanged
  }

  normalizeForSupabase(collectionName, data) {
    const out = { ...data };

    if (collectionName === 'serviceProviders') {
      if (out.service !== undefined && out.serviceCategory === undefined) {
        out.serviceCategory = out.service;
      }
      if (!out.name) out.name = 'Unknown';
      if (!out.email) out.email = '';
      if (!out.serviceCategory) out.serviceCategory = out.service || 'General';
    }

    if (collectionName === 'users' || collectionName === 'admins') {
      if (!out.name) out.name = out.displayName || 'Unknown';
      if (!out.email) out.email = '';
    }

    return out;
  }

  serialiseValue(value) {
    if (value === null || value === undefined) return null;
    if (value?.toDate) return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  parseExperienceYears(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const match = String(value).match(/\d+/);
    return match ? Number.parseInt(match[0], 10) : null;
  }

  async getFirebaseAdminIds() {
    const adminIds = new Set();

    const usersSnap = await getDocs(collection(db, 'users'));
    usersSnap.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const role = (data.userType || data.role || '').toLowerCase();
      if (role === 'admin' || role === 'superadmin') {
        adminIds.add(docSnap.id);
      }
    });

    const adminsSnap = await getDocs(collection(db, 'admins'));
    adminsSnap.docs.forEach((docSnap) => {
      adminIds.add(docSnap.id);
    });

    return adminIds;
  }

  buildDirectTablePayload(collectionName, docId, rawData) {
    const payload = {
      firebase_id: docId,
      data: rawData,
    };

    if (collectionName === 'serviceProviders') {
      payload.name = rawData.name || 'Unknown';
      payload.email = rawData.email || '';
      payload.phone = rawData.phone || null;
      payload.servicecategory = rawData.serviceCategory || rawData.service || 'General';
      payload.experience = this.parseExperienceYears(rawData.experience);
      payload.rating = rawData.rating ?? 0;
      payload.completedjobs = rawData.completedJobs ?? 0;
      payload.isverified = rawData.isVerified ?? false;
      payload.profilepicture = rawData.profilePicture || null;
      payload.bio = rawData.bio || null;
      payload.hourlyrate = rawData.hourlyRate ?? null;
      payload.availability = rawData.availability ?? null;
      payload.address = rawData.address || null;
      payload.createdat = rawData.createdAt || null;
      return payload;
    }

    if (collectionName === 'bookings') {
      payload.customerid = rawData.customerId || rawData.clientId || null;
      payload.customername = rawData.customerName || null;
      payload.customeremail = rawData.customerEmail || null;
      payload.customerphone = rawData.customerPhone || null;
      payload.providerid = rawData.providerId || null;
      payload.providername = rawData.providerName || null;
      payload.servicecategory = rawData.serviceCategory || rawData.service || rawData.serviceName || 'General';
      payload.servicename = rawData.serviceName || null;
      payload.description = rawData.description || null;
      payload.address = rawData.address || null;
      payload.preferreddate = rawData.preferredDate || rawData.scheduledDate || null;
      payload.preferredtime = rawData.preferredTime || rawData.scheduledTime || null;
      payload.status = rawData.status || 'pending';
      payload.urgencymode = rawData.urgencyMode || null;
      payload.visitfee = rawData.visitFee ?? null;
      payload.hourlyrate = rawData.hourlyRate ?? null;
      payload.estimatedprice = rawData.estimatedPrice ?? rawData.price ?? null;
      payload.finalprice = rawData.finalPrice ?? null;
      payload.calculatedtotal = rawData.calculatedTotal ?? null;
      payload.totalhoursworked = rawData.totalHoursWorked ?? null;
      payload.worksessions = rawData.workSessions ?? null;
      payload.statushistory = rawData.statusHistory ?? null;
      payload.pricingbreakdown = rawData.pricingBreakdown ?? null;
      payload.customerlatitude = rawData.customerLatitude ?? null;
      payload.customerlongitude = rawData.customerLongitude ?? null;
      payload.createdat = rawData.createdAt || null;
      payload.updatedat = rawData.updatedAt || null;
      payload.completedat = rawData.completedAt || null;
      return payload;
    }

    if (collectionName === 'reviews') {
      payload.bookingid = rawData.bookingId || rawData.bookingID || rawData.bookingid || rawData.booking || rawData.reviewId || docId;
      payload.customerid = rawData.customerId || rawData.clientId || null;
      payload.providerid = rawData.providerId || null;
      payload.rating = rawData.rating ?? null;
      payload.comment = rawData.comment || null;
      payload.createdat = rawData.createdAt || null;
      return payload;
    }

    return payload;
  }

  /**
   * Build a Supabase payload.
   * Direct tables keep their flat columns populated because the current
   * Supabase schema enforces NOT NULL constraints on them.
   */
  prepareForUpsert(collectionName, docId, rawData) {
    const jsonSafe = {};
    for (const [key, val] of Object.entries(rawData)) {
      jsonSafe[key] = this.serialiseValue(val);
    }

    if (['serviceProviders', 'bookings', 'reviews'].includes(collectionName)) {
      return this.buildDirectTablePayload(collectionName, docId, jsonSafe);
    }

    return {
      firebase_id: docId,
      data: jsonSafe,
    };
  }

  /**
   * Sync a document from Firebase to Supabase
   */
  async syncToSupabase(collectionName, docId, data, operation = 'upsert') {
    if (!this.syncEnabled) return;

    try {
      const targetTable = this.resolveSupabaseTable(collectionName, data);

      if (operation === 'delete') {
        const { error } = await supabase
          .from(targetTable)
          .delete()
          .eq('firebase_id', docId);

        if (error) throw error;
        console.log(`✅ Deleted from Supabase: ${collectionName}/${docId}`);
        return;
      }

      const normalized = this.normalizeForSupabase(collectionName, data);
      const payload = this.prepareForUpsert(collectionName, docId, normalized);

      const { error } = await supabase
        .from(targetTable)
        .upsert(payload, { onConflict: 'firebase_id' });

      if (error) {
        console.error(`❌ Supabase upsert error for ${collectionName}/${docId}:`, error.message);
        throw error;
      }

      console.log(`✅ Synced to Supabase: ${collectionName}/${docId}`);
    } catch (error) {
      console.error(`❌ Supabase sync error for ${collectionName}/${docId}:`, error.message || error);
      this.syncQueue.push({ collectionName, docId, data, operation });
    }
  }

  /**
   * Sync a document from Supabase to Firebase.
   * Handles both the JSONB `data` column format and flat rows.
   * NOTE: This method intentionally does NOT catch errors — callers are
   * responsible for catching so permission-denied can be detected properly.
   */
  async syncToFirebase(collectionName, supabaseRow, operation = 'upsert') {
    if (!this.syncEnabled) return;

    const { firebase_id, id, created_at, updated_at, data: jsonbData, ...rest } = supabaseRow;

    // Prefer the JSONB `data` column; fall back to remaining flat columns
    const docData = (jsonbData && typeof jsonbData === 'object') ? jsonbData : rest;

    if (operation === 'delete' && firebase_id) {
      const docRef = doc(db, collectionName, firebase_id);
      await deleteDoc(docRef);
      console.log(`✅ Deleted from Firebase: ${collectionName}/${firebase_id}`);
    } else if (firebase_id) {
      // setDoc with merge:false creates the doc if missing, replaces if present.
      // updateDoc would throw on a non-existent doc (the orphaned-row case).
      const docRef = doc(db, collectionName, firebase_id);
      await setDoc(docRef, docData);
      console.log(`✅ Upserted in Firebase: ${collectionName}/${firebase_id}`);
    } else {
      // New record — for service providers prefer stable uid-based document IDs.
      // Random IDs become orphaned from Firebase Auth users.
      const preferredId = collectionName === 'serviceProviders' ? docData?.uid : null;

      if (preferredId) {
        await setDoc(doc(db, collectionName, preferredId), docData, { merge: true });
        await supabase
          .from(collectionName)
          .update({ firebase_id: preferredId })
          .eq('id', id);
        console.log(`✅ Created in Firebase: ${collectionName}/${preferredId}`);
      } else {
        const docRef = await addDoc(collection(db, collectionName), docData);
        await supabase
          .from(collectionName)
          .update({ firebase_id: docRef.id })
          .eq('id', id);
        console.log(`✅ Created in Firebase: ${collectionName}/${docRef.id}`);
      }
    }
  }

  /**
   * Temporarily unsubscribe all Supabase realtime channels.
   * Called before forced migration to prevent the WebSocket events
   * triggered by our own writes from looping back.
   */
  async pauseSupabaseListeners() {
    for (const ch of this.channels) {
      try { await supabase.removeChannel(ch); } catch (_) { }
    }
    this.channels = [];
    console.log('⏸️ Supabase realtime listeners paused');
  }

  /**
   * Re-subscribe Supabase realtime channels after migration completes.
   */
  resumeSupabaseListeners() {
    const supabaseTables = ['customers', 'admins', 'serviceProviders', 'bookings', 'reviews'];
    supabaseTables.forEach(t => {
      const ch = this.setupSupabaseListener(t);
      this.channels.push(ch);
    });
    console.log('▶️ Supabase realtime listeners resumed');
  }

  /**
   * Setup Firebase to Supabase real-time sync for a collection
   */
  setupFirebaseListener(collectionName) {
    const collectionRef = collection(db, collectionName);

    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      if (this.migrationRunning) return; // extra guard for in-flight snapshots

      snapshot.docChanges().forEach((change) => {
        const docId = change.doc.id;
        const data = change.doc.data();

        if (change.type === 'added' || change.type === 'modified') {
          this.syncToSupabase(collectionName, docId, data, 'upsert');
        } else if (change.type === 'removed') {
          this.syncToSupabase(collectionName, docId, null, 'delete');
        }
      });
    }, (error) => {
      console.error(`❌ Firebase listener error for ${collectionName}:`, error);
    });

    this.listeners.push(unsubscribe);
    console.log(`🔄 Firebase listener setup for: ${collectionName}`);
  }

  /**
   * Setup Supabase to Firebase real-time sync for a table
   */
  setupSupabaseListener(tableName) {
    const channel = supabase
      .channel(`${tableName}_changes_${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        (payload) => {
          const row = payload.new || payload.old || {};

          // If firebase_id is already set, this change came FROM Firebase.
          // Skip syncing back to avoid an infinite loop.
          if (row.firebase_id) {
            return;
          }

          console.log(`🔄 Supabase change detected in ${tableName} (external edit)`);

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            this.syncToFirebase(tableName, payload.new, 'upsert').catch(err =>
              console.error(`❌ Firebase sync error for ${tableName}:`, err)
            );
          } else if (payload.eventType === 'DELETE') {
            this.syncToFirebase(tableName, payload.old, 'delete').catch(err =>
              console.error(`❌ Firebase sync error for ${tableName}:`, err)
            );
          }
        }
      )
      .subscribe();

    console.log(`🔄 Supabase listener setup for: ${tableName}`);
    return channel;
  }

  /**
   * Initialize bidirectional sync for all collections
   */
  initializeBidirectionalSync() {
    // Firebase collections to listen on
    const firebaseCollections = ['users', 'admins', 'serviceProviders', 'bookings', 'reviews'];
    // Supabase tables to listen on (users split into customers + admins)
    const supabaseTables = ['customers', 'admins', 'serviceProviders', 'bookings', 'reviews'];

    console.log('🚀 Initializing bidirectional sync...');

    firebaseCollections.forEach(c => this.setupFirebaseListener(c));
    supabaseTables.forEach(t => {
      const ch = this.setupSupabaseListener(t);
      this.channels.push(ch);
    });

    console.log('✅ Bidirectional sync initialized for all collections');
  }

  /**
   * Full bidirectional reconciliation.
   *
   * For each collection/table pair:
   *   1. Push every Firebase doc → Supabase (upsert by firebase_id)
   *   2. Pull every Supabase row that has NO firebase_id (or whose firebase_id
   *      doesn't exist in Firebase) → create it in Firebase and write the new
   *      firebase_id back to Supabase so the counts match permanently.
   */
  async migrateFirebaseToSupabase() {
    // Deduplicated Firebase collections to read
    const fbCollections = ['users', 'admins', 'serviceProviders', 'bookings', 'reviews'];

    this.migrationRunning = true;
    console.log('🔄 Starting full two-way sync...');

    // Unsubscribe Supabase realtime channels so our own writes don't loop back
    await this.pauseSupabaseListeners();

    try {
      // ── Step 1: Firebase → Supabase ──────────────────────────────────────────
      for (const collectionName of fbCollections) {
        try {
          const snapshot = await getDocs(collection(db, collectionName));
          let count = 0;
          for (const docSnap of snapshot.docs) {
            await this.syncToSupabase(collectionName, docSnap.id, docSnap.data(), 'upsert');
            count++;
          }
          console.log(`✅ Firebase→Supabase: pushed ${count} docs from ${collectionName}`);
        } catch (err) {
          console.error(`❌ Firebase→Supabase failed for ${collectionName}:`, err.message);
        }
      }

      // ── Step 1.5: Clean stale Supabase admin rows ─────────────────────────
      try {
        const validAdminIds = await this.getFirebaseAdminIds();
        const { data: adminRows, error } = await supabase
          .from('admins')
          .select('id, firebase_id');

        if (error) throw error;

        const staleRowIds = (adminRows || [])
          .filter((row) => !row.firebase_id || !validAdminIds.has(row.firebase_id))
          .map((row) => row.id);

        if (staleRowIds.length > 0) {
          const { error: deleteError } = await supabase
            .from('admins')
            .delete()
            .in('id', staleRowIds);

          if (deleteError) throw deleteError;
          console.log(`✅ Cleaned ${staleRowIds.length} stale rows from Supabase admins`);
        } else {
          console.log('✅ admins: no stale Supabase rows found');
        }
      } catch (err) {
        console.error(`❌ Admin cleanup failed: ${err.message}`);
      }

      // ── Step 2: Supabase → Firebase (orphaned rows only) ─────────────────────
      // Tables that map 1-to-1 with a Firebase collection
      const directTables = ['serviceProviders', 'bookings', 'reviews'];

      for (const tableName of directTables) {
        try {
          // Get all firebase_ids already in Firebase
          const snap = await getDocs(collection(db, tableName));
          const fbIds = new Set(snap.docs.map(d => d.id));

          // Get all Supabase rows
          const { data: rows, error } = await supabase.from(tableName).select('*');
          if (error) throw error;

          let synced = 0;
          let permissionDenied = 0;
          for (const row of rows || []) {
            // Row is orphaned if it has no firebase_id, or its firebase_id doesn't exist in Firebase
            if (!row.firebase_id || !fbIds.has(row.firebase_id)) {
              try {
                await this.syncToFirebase(tableName, row, 'upsert');
                synced++;
              } catch (err) {
                if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
                  permissionDenied++;
                  console.warn(`⚠️ Permission denied writing to Firebase ${tableName}. Publish firestore.rules from project root to Firebase Console.`);
                } else {
                  console.error(`❌ Failed to sync Supabase row to Firebase (${tableName}):`, err.message);
                }
              }
            }
          }
          if (synced > 0) {
            console.log(`✅ Supabase→Firebase: created ${synced} missing docs in ${tableName}`);
          } else if (permissionDenied > 0) {
            console.warn(`⚠️ ${tableName}: ${permissionDenied} orphaned rows blocked by Firestore security rules — publish firestore.rules to Firebase Console.`);
          } else {
            console.log(`✅ ${tableName}: no orphaned Supabase rows found`);
          }
        } catch (err) {
          console.error(`❌ Supabase→Firebase failed for ${tableName}:`, err.message);
        }
      }

      console.log('🎉 Full two-way sync complete!');
    } finally {
      this.migrationRunning = false;
      // Re-subscribe Supabase channels — use a short delay so the DB
      // finishes committing migration writes before we start listening again.
      await new Promise(resolve => setTimeout(resolve, 1500));
      this.resumeSupabaseListeners();
    }
  }

  /**
   * Perform initial data migration from Supabase to Firebase
   */
  async migrateSupabaseToFirebase() {
    await this.migrateFirebaseToSupabase(); // now fully bidirectional
  }

  /**
   * Retry failed sync operations
   */
  async retryFailedSyncs() {
    const failedSyncs = [...this.syncQueue];
    this.syncQueue = [];

    for (const sync of failedSyncs) {
      await this.syncToSupabase(
        sync.collectionName,
        sync.docId,
        sync.data,
        sync.operation
      );
    }
  }

  /**
   * Stop all listeners
   */
  stopAllListeners() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
    console.log('🛑 All sync listeners stopped');
  }

  /**
   * Enable/disable sync
   */
  setSyncEnabled(enabled) {
    this.syncEnabled = enabled;
    console.log(`🔄 Sync ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const syncService = new DatabaseSyncService();

export default syncService;
